import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { loginUser, clearError } from '../redux/slices/authSlice';
import LoginForm from '../components/auth/LoginForm';

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, loading, error } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleLogin = async (credentials) => {
    const result = await dispatch(loginUser(credentials));
    if (loginUser.fulfilled.match(result)) {
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-gray-50 dark:bg-gray-900">
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-primary-100 dark:from-gray-900 dark:via-gray-800 dark:to-primary-900/20" />
      <div className="absolute inset-0 bg-grid opacity-50 dark:opacity-20" />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="relative w-full flex"
      >
        <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12">
          <div className="relative max-w-lg">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative z-10"
            >
              <div className="w-72 h-72 mx-auto relative">
                <div className="absolute inset-0 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full opacity-20 blur-3xl animate-pulse-slow" />
                <div className="absolute inset-4 bg-gradient-to-br from-primary-300 to-primary-500 rounded-full opacity-30 blur-2xl" />
                <div className="absolute inset-8 bg-gradient-to-br from-primary-200 to-primary-400 rounded-full opacity-40" />
                <div className="absolute inset-16 bg-gradient-to-br from-primary-100 to-primary-300 rounded-full opacity-50" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-32 h-32 rounded-3xl bg-white/90 dark:bg-gray-800/90 backdrop-blur shadow-soft-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-3xl font-bold bg-gradient-to-br from-primary-600 to-primary-400 bg-clip-text text-transparent">
                        HP
                      </div>
                      <div className="text-[8px] font-medium text-gray-400 dark:text-gray-500 mt-0.5 tracking-widest uppercase">
                        Shop
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center mt-8"
            >
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white font-display">
                Haider Pan Shop
              </h1>
              <p className="text-lg text-gray-500 dark:text-gray-400 mt-3 leading-relaxed">
                Complete POS and inventory management solution.
                Manage sales, track stock, and grow your business.
              </p>

              <div className="flex items-center justify-center gap-6 mt-6">
                <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
                  <div className="w-1.5 h-1.5 rounded-full bg-success-500" />
                  Inventory Management
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                  POS System
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
                  <div className="w-1.5 h-1.5 rounded-full bg-warning-500" />
                  Reports
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 lg:p-12">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="w-full max-w-md"
          >
            <div className="lg:hidden text-center mb-8">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 rounded-2xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mx-auto mb-4"
              >
                <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">HP</span>
              </motion.div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white font-display">
                Haider Pan Shop
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Sign in to your account
              </p>
            </div>

            <LoginForm onSubmit={handleLogin} loading={loading} error={error} />

            <p className="text-center text-sm text-gray-400 dark:text-gray-500 mt-6">
              &copy; {new Date().getFullYear()} Haider Pan Shop. All rights reserved.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
